import { Chip, Link, Typography } from '@material-ui/core';
import { formatDateGeneric } from 'utilities/common';
import { makeStyles } from '@material-ui/core/styles';
import DashboardDataTable from 'modules/common/dashboardDataTable.component';
import PropTypes from 'prop-types';
import React from 'react';

const useStyles = makeStyles((theme) => ({
  fileLink: {
    cursor: 'pointer',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  vipStar: {
    color: theme.palette.warning.main,
    marginLeft: theme.spacing(0.5),
  },
  statusChip: {
    height: 20,
    fontSize: '0.75rem',
  },
  noData: {
    padding: theme.spacing(3),
    textAlign: 'center',
  },
}));

const ClosedFilesGrid = ({ files, isLoading, onFileClick }) => {
  const classes = useStyles();

  const columns = [
    { accessorKey: 'rcName', header: 'RC Name' },
    {
      accessorKey: 'fileNumber',
      header: 'File Number',
      Cell: ({ row }) => (
        <Link
          className={classes.fileLink}
          onClick={() => onFileClick(row.original.fileId)}
          color="primary"
        >
          {row.original.fileNumber}
        </Link>
      ),
    },
    { accessorKey: 'clientName', header: 'Client' },
    {
      accessorKey: 'employeeName',
      header: 'Employee',
      Cell: ({ row }) => (
        <>
          {row.original.employeeName}
          {row.original.isVIP && <span role="img" aria-label="VIP" className={classes.vipStar}>⭐</span>}
        </>
      ),
    },
    {
      accessorKey: 'initDate',
      header: 'Init Date',
      Cell: ({ cell }) => (cell.getValue() ? formatDateGeneric(cell.getValue()) : 'N/A'),
    },
    { accessorKey: 'fileType', header: 'File Type' },
    {
      accessorKey: 'closedDate',
      header: 'Closed Date',
      Cell: ({ cell }) => (cell.getValue() ? formatDateGeneric(cell.getValue()) : 'N/A'),
    },
    {
      accessorKey: 'statusDescription',
      header: 'Status',
      Cell: ({ cell }) => (
        <Chip
          label={cell.getValue()}
          size="small"
          color={cell.getValue() === 'Completed' ? 'primary' : 'default'}
          className={classes.statusChip}
        />
      ),
    },
  ];

  if (isLoading) {
    return (
      <Typography className={classes.noData} color="textSecondary">
        Loading closed files...
      </Typography>
    );
  }

  if (files.length === 0) {
    return (
      <Typography className={classes.noData} color="textSecondary">
        No closed files found in the last 30 days.
      </Typography>
    );
  }

  return (
    <DashboardDataTable
      columns={columns}
      data={files}
      initialSorting={[{ id: 'closedDate', desc: true }]}
    />
  );
};

ClosedFilesGrid.propTypes = {
  files: PropTypes.arrayOf(
    PropTypes.shape({
      fileId: PropTypes.number.isRequired,
      rcName: PropTypes.string.isRequired,
      fileNumber: PropTypes.string.isRequired,
      clientName: PropTypes.string.isRequired,
      employeeName: PropTypes.string,
      isVIP: PropTypes.bool,
      fileType: PropTypes.string,
      initDate: PropTypes.string,
      closedDate: PropTypes.string,
      statusDescription: PropTypes.string,
    }),
  ).isRequired,
  isLoading: PropTypes.bool.isRequired,
  onFileClick: PropTypes.func.isRequired,
};

export default ClosedFilesGrid;
